import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalContainer, ModalContent, ModalHeader, ModalFooter } from 'components/@common/Modal';
import PageContainer from 'components/@layout/PageContainer';
import { Button } from '@material-ui/core';
import useStore from 'store';
import { VoidCallback } from 'types';

export interface PrintModalOptions {
  cancelText?: string;
  printText?: string;
  disablePrintButton?: boolean;
}

export interface PrintModalProps {
  title: string;
  children: ReactElement;
  onClose?: VoidCallback;
  onPrint: VoidCallback;
  options?: PrintModalOptions;
}

const PrintModal = ({
  title,
  children,
  onClose,
  onPrint,
  options
}: PrintModalProps): ReactElement => {
  const { t } = useTranslation('PrintModal');
  const [, { closeModal }] = useStore();

  const handleOnClose = (): void => {
    if (onClose) onClose();
    closeModal();
  };

  const handleOnPrint = (): void => {
    if (onPrint) {
      closeModal();
      onPrint();
    }
  };

  return (
    <PageContainer>
      <ModalContainer
        Header={<ModalHeader title={title} />}
        Footer={
          <ModalFooter
            Actions={
              <>
                <Button
                  aria-label={t('Close Modal')}
                  variant="outlined"
                  color="secondary"
                  disableElevation
                  onClick={handleOnClose}
                >
                  {options?.cancelText || t('Cancel')}
                </Button>
                <Button
                  aria-label={t('Print Modal')}
                  variant="contained"
                  color="primary"
                  disableElevation
                  disabled={options?.disablePrintButton}
                  onClick={handleOnPrint}
                >
                  {options?.printText || t('Print')}
                </Button>
              </>
            }
          />
        }
      >
        <ModalContent>{children}</ModalContent>
      </ModalContainer>
    </PageContainer>
  );
};

export default PrintModal;
